// Shared formatting helpers for the admin console. Kept in one place so the
// several admin tables render identically (and a locale/format change lands
// everywhere at once).

// A dash for an empty/absent string value.
export function show(v: string | null): string {
  return v !== null && v.length > 0 ? v : '—';
}

export function formatBytes(n: number | null): string {
  if (n === null) return '—';
  if (n < 1024) return `${n} B`;
  const kb = n / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

// Byte counts that can reach gigabytes (table sizes, daily traffic): 1024-based
// units up to TB, one decimal. '—' for null.
export function formatSize(n: number | null): string {
  if (n === null) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = n;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return unit === 0 ? `${value} B` : `${value.toFixed(1)} ${units[unit]}`;
}

export function formatConfidence(c: number | null): string {
  return c === null ? '—' : `${Math.round(c * 100)}%`;
}

export function formatDate(iso: string | null): string {
  if (iso === null) return '—';
  return new Date(iso).toLocaleString();
}

// Integer with locale grouping.
export function formatInt(n: number): string {
  return n.toLocaleString();
}

// Tiny-USD amount — enough precision to read sub-dollar sums; '—' for null.
// Used by both Usage dashboards and the Manual review run strip so the same
// dollar figure renders identically everywhere.
export function formatCost(n: number | null): string {
  if (n === null) return '—';
  return `$${n < 1 ? n.toFixed(4) : n.toFixed(2)}`;
}

// Millisecond latency — '123 ms' or '—'.
export function formatLatency(ms: number | null): string {
  return ms !== null ? `${formatInt(ms)} ms` : '—';
}
